<template>
	<div class="column-key-map-wrapper">
    <div :class="`${bulkSelection.length ? 'flex' : 'hidden'} justify-end mb-8 relative`">
      <select
          id="bulk-action"
          class="border border-gray-200 rounded focus:border-green-500 focus:ring-transparent mr-2 bg-right"
          style="width: 200px"
          v-model="bulkAction"
      >
        <option value="" disabled selected>Select Bulk Action</option>
        <option value="disable">Disable</option>
        <option value="enable">Enable</option>
        <option value="remove" v-if="$store.state.hasOwnProperty('exportDetails') ? $store.state.exportDetails.isAdmin: false">Delete</option>
      </select>

      <button
          :disabled="bulkAction === ''"
          class="btn btn-outline-primary relative hover:bg-green-500 hover:text-white"
          @click.prevent="confirmBulkAction = true"
      >
        Bulk
        <confirm-box
          :show="confirmBulkAction"
          :content="`Are you sure you want to ${bulkAction} these modifications?`"
          :button="bulkAction"
          @cancel="confirmBulkAction = false"
          @confirm="doBulkAction()"
        />
      </button>
    </div>

		<draggable v-model="localValue" draggable=".logic-row-wrapper">
			<div v-for="(rulesRow, i) in localValue" class="logic-row-wrapper">
				<div
					class="logic-row-inner-wrapper"
					:style="localValue[i].disabled ? 'filter:grayscale(100%) brightness(0.9) opacity(.5);' : 'filter:grayscale(0%) brightness(1) opacity(1);'"
				>
					<div class="flex items-center justify-between mb-4">
						<input
                :data-w-input="`${dataWInput}-${i}-crm-field-key`"
                data-form-type="other"
                type="text"
                placeholder="crm field key"
                class="w-full border border-gray-200 rounded focus:border-green-500 focus:ring-transparent mr-2"
                v-model="rulesRow.crm_key"
                :disabled="readonly"
            />

						<input
                :data-w-input="`${dataWInput}-${i}-crm-field-value`"
                data-form-type="other"
                type="text"
                placeholder="crm field value"
                class="w-full border border-gray-200 rounded focus:border-green-500 focus:ring-transparent"
                v-model="rulesRow.crm_value"
                :disabled="readonly"
            />
					</div>

					<repeatable-input
						v-model="localValue[i].rules"
						:striped="true"
						:proto="ruleRow"
						:add-icon-to-btm="true"
						remove-icon="fa-times"
            ref="ruleInputRef"
            :disabled="readonly"
					>
						<template v-slot:default="{ row, rowIndex }">
							<div class="flex flex-wrap w-full my-2">
								<div class="flex items-center justify-between w-full">
                  <div class="text-right shrink-0 px-1 my-auto" style="width: 38px">{{ rowIndex ? 'And' : 'If' }}</div>

									<select
                      v-model="row.lead_column"
                      :data-w-input="`${dataWInput}-${i}-rules-${rowIndex}-column`"
                      class="w-full border border-gray-200 rounded focus:border-green-500 focus:ring-transparent mr-2 grow block"
                      ref="leadColumn"
                      @change="leadColumnCheck()"
                      :disabled="readonly"
                  >
										<option value disabled>lead table column</option>
										<option
                        v-for="column in leadColumnOptions"
                        :key="column"
                        :value="column"
                        :hidden="!column || column.startsWith('scn.')"
                    >{{ column }}</option>
									</select>

									<select
                      class="w-full border border-gray-200 rounded focus:border-green-500 focus:ring-transparent mr-2 block grow"
                      v-model="row.operator"
                      :data-w-input="`${dataWInput}-${i}-rules-${rowIndex}-operator`"
                      :disabled="readonly"
                  >
										<option value="is">is</option>
										<option value="isNot">is not</option>
										<option value="greaterThan">is greater than</option>
										<option value="lessThan">is less than</option>
										<option value="contains">contains</option>
										<option value="excludes">excludes</option>
										<option value="startsWith">starts with</option>
										<option value="endsWith">ends with</option>
									</select>

                  <select
                      v-model="row.scope"
                      :data-w-input="`${dataWInput}-${i}-rules-${rowIndex}-scope`"
                      class="block w-full border border-gray-200 rounded focus:border-green-500 focus:ring-transparent shrink"
                      :disabled="readonly"
                  >
                    <option value="all">all</option>
                    <option value="any">any</option>
                  </select>
								</div>

                <textarea
                    type="text"
                    :data-w-input="`${dataWInput}-${i}-rules-${rowIndex}-lead-value`"
                    class="w-full border border-gray-200 rounded focus:border-green-500 focus:ring-transparent mt-2"
                    style="margin-left: 38px"
                    v-model="row.lead_value"
                    placeholder="comma separated"
                    :disabled="readonly"
                ></textarea>
							</div>
						</template>
					</repeatable-input>
				</div>

				<logic-field-controls
					:value.sync="localValue[i]"
					:index="i"
					:field-name="'modification_logic'"
          :selectable="true"
				></logic-field-controls>
			</div>

			<button
        id="new-rule-set-btn"
				slot="footer"
				class="btn btn-outline-primary hover:bg-green-500 hover:text-white"
				@click.prevent="addRulesRow"
			>Add New Rule Set</button>
		</draggable>
	</div>
</template>

<script>
import LogicFieldControls from '../form-ui/LogicFieldControls';
import {leadColumns} from "../../lib/helpers";
import {add as addCustomQuestion} from "../../lib/CustomQuestion";
import draggable from 'vuedraggable';
import ConfirmBox from '../ui-components/ConfirmBox';

export default {
	components: {
		LogicFieldControls,
    ConfirmBox,
		draggable,
	},
	props: {
		leadTableColumns: {type: Array, required: true},
		value: {type: Array, required: true},
		confirmBox: {type: Boolean, default: false},
    dataWInput: {type: String, default: ''},
    readonly: {type: Boolean, default: false},
	},
	data() {
		return {
      bulkAction: '',
      confirmBulkAction: false,
			newRulesRow: {
				crm_key: '',
				crm_value: '',
				disabled: false,
				rules: [],
			},
			ruleRow: [
				{
					lead_column: '',
					operator: 'is',
					scope: 'all',
					lead_value: '',
				}
			],
			disableConfirmBox: false,
			customQuestionID: '',
		};
	},
	computed: {
		localValue: {
			get() {
				let local = [];

				if (this.value.length) {
					this.value.forEach(row => {
						if (typeof row.rules === 'undefined') {
							row.rules = [
								{
									lead_column: row.lead_column,
									operator: row.operator,
									scope: row.scope,
									lead_value: row.lead_value,
								}
							];

							delete row.lead_column;
							delete row.operator;
							delete row.scope;
							delete row.lead_value;
						}

						if (row.rules.length) {
              addCustomQuestion(this, row.rules, 'lead_column', 'leadColumnOptions');
            }

						if (typeof row.disabled === 'undefined') {
              row.disabled = false;
            }

						local.push(row);
					});
				}

				return local;
			},
			set(value) {
				this.$dispatch("input", value);
			}
		},
    bulkSelection() {
      return this.$store.state.hasOwnProperty('exportDetails') ? this.$store.state.exportDetails.current.bulkSelection.modification_logic : false;
    },
		leadColumnOptions() {
			return leadColumns(this.leadTableColumns);
		},
	},
	methods: {
		removeRulesRow(index) {
      console.log(index, this.localValue);
			this.localValue.splice(index, 1);
			this.$dispatch("input", this.localValue);
		},
		disableRulesRow(index) {
			this.localValue[index].disabled = !this.localValue[index].disabled
			this.$dispatch("input", this.localValue)
		},
		bulkRemoveRulesRows() {
			const bulkSelection = this.bulkSelection
			let localValue = this.localValue

			localValue = localValue.filter(function (row, index) {
				return bulkSelection.indexOf(index) < 0
			});

			this.$dispatch("input", localValue);
			this.$store.commit('exportDetails/RESET_BULK_SELECTION', 'modification_logic')
		},
		bulkDisableRulesRows() {
      this.bulkSelection.forEach((index) => this.localValue[index].disabled = true);

			this.$dispatch("input", this.localValue);
      this.$store.commit('exportDetails/RESET_BULK_SELECTION', 'modification_logic')
		},
		bulkEnableRulesRows() {
      this.bulkSelection.forEach((index) => this.localValue[index].disabled = false);

			this.$dispatch("input", this.localValue);
      this.$store.commit('exportDetails/RESET_BULK_SELECTION', 'modification_logic')
		},
		addRulesRow() {
			let temp = JSON.parse(JSON.stringify(this.localValue));

			temp.push(this.newRulesRow);

			this.$dispatch("input", temp);
		},
    toggleBulkIndex(index) {
      this.$store.commit('exportDetails/UPDATE_CURRENT_BULK_SELECTION', {index: index, component: 'modification_logic'});
    },
    doBulkAction() {
      if(this.bulkAction === 'remove') this.bulkRemoveRulesRows();
      if(this.bulkAction === 'disable') this.bulkDisableRulesRows();
      if(this.bulkAction === 'enable') this.bulkEnableRulesRows();
    },
    leadColumnCheck() {
      let ref = this.$refs.leadColumn;

      for (let logicRow of ref) {
        if (logicRow.classList.contains('lead-column-error')) {
          logicRow.classList.remove('lead-column-error');
        }
      }
    }
	}
}
</script>

<style lang="scss" scoped>
.logic-row-wrapper {
	border: 1px solid rgba(black, .1);
	border-radius: .25rem;
	padding: .5rem;
	background: rgba(black, .05);
	margin-bottom: .5rem;
	position: relative;
}

.logic-row-inner-wrapper {
	transition: filter .25s ease-out;
}

.logic-row-wrapper, .logic-row {
	width: 97%;

	& > * > * {
		margin: 5px;
	}
}

label {
	font-size: .8rem;
}
</style>
<style lang="scss">
.comp-control-confirm {
  text-transform: capitalize !important;
}
</style>
